import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../lib/auth';
import {
  getGetrixCredentialsStatus,
  clearGetrixCredentials,
  setGetrixPrompts,
  setGetrixVideo,
  uploadGetrixAsset,
  deleteGetrixAsset,
  startGetrixOnboarding,
  getGetrixOnboardingStatus,
  sendGetrixOnboardingCommand,
  getAutopublishFilter,
  setAutopublishFilter,
  fetchFilterOptions,
} from '../lib/api';
import FilterPanel, { FILTER_KEYS, countActiveFilters } from '../components/FilterPanel';

export default function Impostazioni() {
  const { user } = useAuth();
  const orgName = user?.organization?.name;
  // Prospect: render dedicato (solo notifiche WhatsApp per ora)
  if (user?.is_prospect) return <ProspectImpostazioni user={user} />;

  const [credStatus, setCredStatus] = useState(null);
  const [session, setSession]       = useState(null);
  const [loading, setLoading]       = useState(true);
  const [err, setErr]               = useState(null);

  // Wizard form
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPwd, setShowPwd] = useState(false);
  const [otp, setOtp] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Prompt descrizione per-org
  const [promptForm, setPromptForm] = useState({ residenziale: '', commerciale: '' });
  const [promptSaving, setPromptSaving] = useState(false);
  const [promptSaved, setPromptSaved] = useState(false);
  const promptInited = useRef(false);

  // Autopubblicazione: lista di regole {percentuale, filter:URLSearchParams}
  const [autoRules, setAutoRules] = useState([]);
  const [autoFilterOptions, setAutoFilterOptions] = useState({});
  const [autoFilterSaving, setAutoFilterSaving] = useState(false);
  const [autoFilterSaved, setAutoFilterSaved] = useState(false);
  const [autoPanelRuleIdx, setAutoPanelRuleIdx] = useState(null); // indice della regola in edit; null = chiuso

  const pollTimer = useRef(null);

  const reload = async () => {
    try {
      const [c, s] = await Promise.all([
        getGetrixCredentialsStatus(),
        getGetrixOnboardingStatus(),
      ]);
      setCredStatus(c);
      setSession(s?.session || null);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || 'Errore caricamento');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  // Helpers regola → URLSearchParams ↔ oggetto piatto
  const objToParams = (obj) => {
    const p = new URLSearchParams();
    Object.entries(obj || {}).forEach(([k, v]) => {
      if (v === null || v === undefined || v === '') return;
      p.set(k, String(v));
    });
    return p;
  };
  const paramsToObj = (params) => {
    const obj = {};
    FILTER_KEYS.forEach((k) => {
      const v = params.get(k);
      if (v !== null && v !== '') obj[k] = v;
    });
    const view = params.get('view');
    if (view) obj.view = view;
    return obj;
  };

  useEffect(() => {
    fetchFilterOptions().then(setAutoFilterOptions).catch(() => {});
    getAutopublishFilter().then((res) => {
      const rules = (res?.rules || []).map((r) => ({
        percentuale: Number(r.percentuale) || 0,
        filter: objToParams(r.filter || {}),
      }));
      setAutoRules(rules);
    }).catch(() => {});
  }, []);

  const addRule = () => {
    setAutoRules((rs) => [...rs, { percentuale: 0, filter: new URLSearchParams() }]);
    setAutoFilterSaved(false);
  };
  const removeRule = (idx) => {
    setAutoRules((rs) => rs.filter((_, i) => i !== idx));
    setAutoFilterSaved(false);
  };
  const updateRulePerc = (idx, val) => {
    setAutoRules((rs) => rs.map((r, i) => i === idx ? { ...r, percentuale: val } : r));
    setAutoFilterSaved(false);
  };
  const updateRuleFilter = (idx, nextParams) => {
    const p = nextParams instanceof URLSearchParams ? nextParams : new URLSearchParams(nextParams);
    setAutoRules((rs) => rs.map((r, i) => i === idx ? { ...r, filter: p } : r));
    setAutoFilterSaved(false);
  };

  const totalPerc = autoRules.reduce((s, r) => s + (Number(r.percentuale) || 0), 0);

  const handleSaveAutoFilter = async () => {
    setAutoFilterSaving(true); setErr(null); setAutoFilterSaved(false);
    try {
      const payload = autoRules.map((r) => ({
        percentuale: Number(r.percentuale) || 0,
        filter: paramsToObj(r.filter),
      }));
      await setAutopublishFilter(payload);
      setAutoFilterSaved(true);
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || 'Errore salvataggio filtro');
    } finally { setAutoFilterSaving(false); }
  };

  // Inizializza i prompt una sola volta, al primo caricamento di credStatus
  // (così il polling della sessione non sovrascrive le modifiche dell'utente).
  useEffect(() => {
    if (credStatus && !promptInited.current) {
      setPromptForm({
        residenziale: credStatus.getrix_prompt_residenziale || '',
        commerciale: credStatus.getrix_prompt_commerciale || '',
      });
      promptInited.current = true;
    }
  }, [credStatus]);

  const handleSavePrompts = async () => {
    setPromptSaving(true); setErr(null); setPromptSaved(false);
    try {
      await setGetrixPrompts(promptForm);
      setPromptSaved(true);
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || 'Errore salvataggio prompt');
    } finally { setPromptSaving(false); }
  };

  // Polling stato sessione (ogni 2s mentre c'è una sessione non-terminal)
  useEffect(() => {
    if (!session || session.is_terminal) {
      if (pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null; }
      return;
    }
    pollTimer.current = setInterval(reload, 2000);
    return () => { if (pollTimer.current) clearInterval(pollTimer.current); };
  }, [session?.id, session?.state, session?.is_terminal]);

  const handleStart = async (e) => {
    e?.preventDefault();
    setSubmitting(true); setErr(null);
    try {
      const res = await startGetrixOnboarding(form);
      setSession(res.session);
      setForm({ email: '', password: '' });
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || 'Errore avvio');
    } finally { setSubmitting(false); }
  };

  const handleCmd = async (command, otpCode = null) => {
    setSubmitting(true); setErr(null);
    try {
      const res = await sendGetrixOnboardingCommand({ command, otp: otpCode });
      setSession(res.session);
      if (command === 'submit_otp') setOtp('');
      await reload();
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || 'Errore');
    } finally { setSubmitting(false); }
  };

  const handleClear = async () => {
    if (!window.confirm('Cancellare credenziali e cookies Getrix?')) return;
    setSubmitting(true); setErr(null);
    try {
      await clearGetrixCredentials();
      setSession(null);
      await reload();
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || 'Errore');
    } finally { setSubmitting(false); }
  };

  if (loading) return <main className="content"><p style={muted}>Caricamento…</p></main>;

  // Stato corrente del wizard
  const state = session?.state;
  const isActive = session && !session.is_terminal;

  return (
    <main className="content">
      <div className="page__header">
        <div className="page__title-wrap">
          <h1 className="page__title">Impostazioni</h1>
          <p className="page__sub">{orgName ? `Organizzazione: ${orgName}` : 'Profilo organizzazione'}</p>
        </div>
      </div>

      <section style={card}>
        <header style={cardHead}>
          <div style={cardTitle}>Connessione a Getrix (immobiliare.it)</div>
          <div style={cardSub}>
            Procedura guidata di prima connessione: login, conferma email, OTP via SMS.
            Le credenziali sono salvate cifrate (AES-256) nel database.
          </div>
        </header>

        {/* Stato attuale */}
        <div style={statusBox}>
          <Row label="Credenziali">
            {credStatus?.has_credentials
              ? <Tag tone="ok">{credStatus.email_masked || 'configurate'}</Tag>
              : <Tag tone="warn">non configurate</Tag>}
          </Row>
          <Row label="Cookies di sessione">
            {credStatus?.has_cookies
              ? <Tag tone="ok">✓ device trusted
                  {credStatus.cookies_updated_at && (
                    <em style={{ marginLeft: 8, opacity: 0.7, fontStyle: 'normal' }}>
                      ({fmtDate(credStatus.cookies_updated_at)})
                    </em>
                  )}
                </Tag>
              : <Tag tone="warn">assenti</Tag>}
          </Row>
        </div>

        {err && <div style={errBox}>{err}</div>}

        {/* === WIZARD === */}
        {!isActive && (
          <form onSubmit={handleStart} style={{ marginTop: 18 }}>
            <h3 style={h3}>
              {credStatus?.has_cookies ? 'Riconfigura connessione' : 'Avvia connessione a Getrix'}
            </h3>
            <p style={sub}>
              {credStatus?.has_cookies
                ? 'La connessione attuale funziona. Procedi solo se vuoi reimpostarla.'
                : 'Inserisci le credenziali Getrix dell\'organizzazione. Aprirò un browser headless e ti guiderò attraverso conferma email e OTP via SMS.'}
            </p>
            <div style={fieldRow}>
              <label style={lbl}>Email Getrix</label>
              <input type="email" required value={form.email}
                     onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                     placeholder="info@studio.it" style={input}/>
            </div>
            <div style={fieldRow}>
              <label style={lbl}>Password Getrix</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input type={showPwd ? 'text' : 'password'} required value={form.password}
                       autoComplete="new-password"
                       onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
                       style={{ ...input, flex: 1 }}/>
                <button type="button" className="btn btn--ghost" onClick={() => setShowPwd(s => !s)}>
                  {showPwd ? 'Nascondi' : 'Mostra'}
                </button>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
              {credStatus?.has_credentials && (
                <button type="button" className="btn btn--ghost" onClick={handleClear} disabled={submitting}>
                  Cancella tutto
                </button>
              )}
              <button type="submit" className="btn btn--solid"
                      disabled={submitting || !form.email || !form.password}>
                {submitting ? 'Avvio…' : 'Avvia connessione'}
              </button>
            </div>
          </form>
        )}

        {/* === STEP attivi della macchina ===*/}
        {isActive && (
          <div style={{ marginTop: 18 }}>
            <StepIndicator state={state} />

            {(state === 'starting' || state === 'logging_in') && (
              <Panel tone="info">
                <strong>Step 1/3 — Login in corso…</strong><br/>
                Il browser headless sta accedendo a Getrix con le credenziali fornite. Attendi qualche secondo.
              </Panel>
            )}

            {state === 'awaiting_email_confirm' && (
              <Panel tone="action">
                <strong>Step 2/3 — Conferma la tua email</strong>
                <p style={{ margin: '8px 0' }}>
                  Getrix ti ha inviato una mail di conferma. Apri la tua casella ({credStatus?.email_masked || 'email associata'}),
                  clicca sul link di conferma, poi torna qui e premi il bottone.
                </p>
                <button className="btn btn--solid" disabled={submitting}
                        onClick={() => handleCmd('email_confirmed')}>
                  ✓ Ho confermato l'email
                </button>
                {' '}
                <button className="btn btn--ghost" disabled={submitting}
                        onClick={() => handleCmd('abort')}>Annulla</button>
              </Panel>
            )}

            {state === 'requesting_otp' && (
              <Panel tone="action">
                <strong>Step 3/3 — Richiedi codice OTP via SMS</strong>
                <p style={{ margin: '8px 0' }}>
                  Premendo qui sotto, Getrix invierà un SMS al numero registrato. Tienilo a portata di mano.
                </p>
                <button className="btn btn--solid" disabled={submitting}
                        onClick={() => handleCmd('request_otp')}>
                  📱 Invia OTP via SMS
                </button>
                {' '}
                <button className="btn btn--ghost" disabled={submitting}
                        onClick={() => handleCmd('abort')}>Annulla</button>
              </Panel>
            )}

            {state === 'awaiting_otp_code' && (
              <Panel tone="action">
                <strong>Step 3/3 — Inserisci l'OTP ricevuto via SMS</strong>
                <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
                  <input
                    autoFocus
                    inputMode="numeric"
                    pattern="\d{4,8}"
                    maxLength={8}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                    placeholder="es. 123456"
                    style={{ ...input, maxWidth: 200, fontSize: 18, letterSpacing: 4, textAlign: 'center' }}
                  />
                  <button className="btn btn--solid"
                          disabled={submitting || !otp || otp.length < 4}
                          onClick={() => handleCmd('submit_otp', otp)}>
                    Conferma OTP
                  </button>
                  <button className="btn btn--ghost" disabled={submitting}
                          onClick={() => handleCmd('abort')}>Annulla</button>
                </div>
              </Panel>
            )}

            {(state === 'submitting_otp' || state === 'dumping_cookies') && (
              <Panel tone="info">
                <strong>Finalizzazione…</strong><br/>
                Sto verificando OTP ed estraendo i cookies di sessione. Pochi secondi.
              </Panel>
            )}

            {state === 'completed' && (
              <Panel tone="ok">
                <strong>✓ Connessione completata.</strong> I cookies sono stati salvati cifrati nel database.
                Da ora puoi pubblicare gli annunci su Getrix dalla pagina Immobili.
              </Panel>
            )}

            {state === 'failed' && (
              <Panel tone="err">
                <strong>✗ Onboarding fallito.</strong>
                {session?.error_message && <div style={{ marginTop: 6, fontSize: 12, opacity: 0.85 }}>
                  {session.error_message}
                </div>}
              </Panel>
            )}

            {session?.is_terminal && (
              <div style={{ marginTop: 12, textAlign: 'right' }}>
                <button className="btn btn--ghost" onClick={() => { setSession(null); reload(); }}>
                  Chiudi
                </button>
              </div>
            )}
          </div>
        )}
      </section>

      {/* === Prompt descrizione annunci (per organizzazione) === */}
      <section style={{ ...card, marginTop: 16 }}>
        <header style={cardHead}>
          <div style={cardTitle}>Descrizione annunci — istruzioni AI</div>
          <div style={cardSub}>
            Sono le istruzioni con cui l'AI scrive la descrizione degli annunci di questa
            organizzazione, al momento della pubblicazione su Getrix. Non è un template con
            campi da riempire: i dati dell'immobile (indirizzo, mq, vani…) vengono forniti
            all'AI separatamente. Qui scrivi solo <em>come</em> deve essere il testo (stile,
            sezioni, scheda tecnica).
          </div>
        </header>

        <div style={fieldRow}>
          <label style={lbl}>Immobili residenziali</label>
          <textarea
            rows={12}
            value={promptForm.residenziale}
            onChange={(e) => { setPromptForm(f => ({ ...f, residenziale: e.target.value })); setPromptSaved(false); }}
            style={textarea}
          />
        </div>

        <div style={fieldRow}>
          <label style={lbl}>Immobili commerciali / industriali</label>
          <textarea
            rows={12}
            value={promptForm.commerciale}
            onChange={(e) => { setPromptForm(f => ({ ...f, commerciale: e.target.value })); setPromptSaved(false); }}
            style={textarea}
          />
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
          {promptSaved && <span style={{ fontSize: 12, color: 'oklch(0.45 0.13 145)' }}>✓ Salvato</span>}
          <button type="button" className="btn btn--solid" disabled={promptSaving} onClick={handleSavePrompts}>
            {promptSaving ? 'Salvataggio…' : 'Salva istruzioni'}
          </button>
        </div>
      </section>

      {/* === Allegati pubblicazione annunci (video + APE + foto fisse) === */}
      <GetrixAssetsSection
        assets={credStatus?.getrix_assets}
        onReload={reload}
      />

      {/* === Filtro autopubblicazione su Getrix (regole ripetibili con percentuale) === */}
      <section style={{ ...card, marginTop: 16 }}>
        <header style={cardHead}>
          <div style={cardTitle}>Autopubblicazione annunci su Getrix</div>
          <div style={cardSub}>
            Definisci una o più <em>regole</em>: a ciascuna assegni una percentuale e un
            filtro (gli stessi criteri della pagina Immobili). Il cron pubblicherà gli
            immobili dividendo gli slot in base alle percentuali. La somma dovrebbe essere
            100%. Per ora il cron non è attivo: si salva soltanto.
          </div>
        </header>

        {autoRules.length === 0 && (
          <p style={{ fontSize: 13, color: 'oklch(0.50 0 0)', margin: '0 0 12px 0' }}>
            Nessuna regola. Aggiungine una per iniziare.
          </p>
        )}

        {autoRules.map((rule, idx) => {
          const activeN = countActiveFilters(rule.filter);
          return (
            <div key={idx} style={ruleRow}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, color: 'oklch(0.45 0 0)' }}>Regola {idx + 1}</span>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
                  <input
                    type="number" min={0} max={100} step={1}
                    value={rule.percentuale}
                    onChange={(e) => updateRulePerc(idx, e.target.value === '' ? 0 : Number(e.target.value))}
                    style={{ ...input, width: 80, padding: '6px 8px' }}
                  />
                  <span>%</span>
                </label>
                <button type="button" className="btn btn--ghost"
                        onClick={() => setAutoPanelRuleIdx(idx)}>
                  Modifica filtro
                </button>
                <span style={{ fontSize: 12.5, color: 'oklch(0.50 0 0)' }}>
                  {activeN === 0 ? 'nessun criterio' : `${activeN} criteri`}
                </span>
                <button type="button" className="btn btn--ghost"
                        style={{ marginLeft: 'auto', color: 'oklch(0.50 0.15 25)' }}
                        onClick={() => removeRule(idx)}>
                  Rimuovi
                </button>
              </div>
            </div>
          );
        })}

        <div style={{ display: 'flex', gap: 10, marginTop: 10, alignItems: 'center' }}>
          <button type="button" className="btn btn--ghost" onClick={addRule}>+ Aggiungi regola</button>
          {autoRules.length > 0 && (
            <span style={{ fontSize: 12.5, color: totalPerc === 100 ? 'oklch(0.45 0.13 145)' : 'oklch(0.50 0.15 25)' }}>
              Totale: {totalPerc}% {totalPerc !== 100 && '(deve fare 100)'}
            </span>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end', alignItems: 'center' }}>
          {autoFilterSaved && <span style={{ fontSize: 12, color: 'oklch(0.45 0.13 145)' }}>✓ Salvato</span>}
          <button type="button" className="btn btn--solid" disabled={autoFilterSaving}
                  onClick={handleSaveAutoFilter}>
            {autoFilterSaving ? 'Salvataggio…' : 'Salva regole'}
          </button>
        </div>
      </section>

      <FilterPanel
        open={autoPanelRuleIdx !== null}
        onClose={() => setAutoPanelRuleIdx(null)}
        searchParams={autoPanelRuleIdx !== null ? autoRules[autoPanelRuleIdx]?.filter || new URLSearchParams() : new URLSearchParams()}
        setSearchParams={(next) => {
          if (autoPanelRuleIdx !== null) updateRuleFilter(autoPanelRuleIdx, next);
        }}
        filterOptions={autoFilterOptions}
        context="immobili"
      />
    </main>
  );
}

// === Helpers UI ===

function GetrixAssetsSection({ assets, onReload }) {
  const [videoUrl, setVideoUrl] = useState('');
  const [savingVideo, setSavingVideo] = useState(false);
  const [savedVideo, setSavedVideo] = useState(false);
  const [uploading, setUploading] = useState(null); // kind in progress
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (assets?.video_youtube_url != null) setVideoUrl(assets.video_youtube_url || '');
  }, [assets?.video_youtube_url]);

  const cardLocal = { background: 'white', border: '1px solid oklch(0.92 0 0)', borderRadius: 12, padding: 20, maxWidth: 720 };
  const cardHeadL = { marginBottom: 14 };
  const cardTitleL = { fontSize: 16, fontWeight: 700 };
  const cardSubL = { color: 'oklch(0.45 0 0)', fontSize: 13, marginTop: 4, lineHeight: 1.5 };

  const saveVideo = async () => {
    setSavingVideo(true); setErr(null); setSavedVideo(false);
    try {
      await setGetrixVideo(videoUrl || null);
      setSavedVideo(true);
      onReload?.();
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || 'Errore salvataggio URL video');
    } finally { setSavingVideo(false); }
  };

  const handleUpload = async (kind, file) => {
    if (! file) return;
    setUploading(kind); setErr(null);
    try {
      await uploadGetrixAsset(kind, file);
      onReload?.();
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || 'Errore upload');
    } finally { setUploading(null); }
  };

  const handleDelete = async (kind) => {
    if (! window.confirm(`Cancellare ${kind}?`)) return;
    setUploading(kind); setErr(null);
    try {
      await deleteGetrixAsset(kind);
      onReload?.();
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || 'Errore cancellazione');
    } finally { setUploading(null); }
  };

  const fotoSlots = Array.from({ length: 9 }, (_, i) => i + 1);
  const apeStatus = assets?.ape ?? { present: false };
  const planimetriaStatus = assets?.planimetria ?? { present: false };
  const fotoStatus = assets?.foto ?? {};

  return (
    <section style={{ ...cardLocal, marginTop: 16 }}>
      <header style={cardHeadL}>
        <div style={cardTitleL}>Allegati pubblicazione annunci</div>
        <div style={cardSubL}>
          File fissi caricati su ogni annuncio Getrix pubblicato: URL del video YouTube
          istituzionale, file APE.pdf, fino a 9 foto fisse dell'agenzia (es. logo, copertine,
          immagini campagna). Le foto vengono ridimensionate a 2400×1600 max al momento dell'upload.
        </div>
      </header>

      {err && (
        <div style={{ background: 'oklch(0.95 0.05 25)', border: '1px solid oklch(0.85 0.13 25)',
                      padding: 10, borderRadius: 8, fontSize: 13, color: 'oklch(0.35 0.13 25)',
                      marginBottom: 12 }}>
          {err}
        </div>
      )}

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
          URL video YouTube
        </label>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            type="url"
            placeholder="https://youtu.be/..."
            value={videoUrl}
            onChange={(e) => { setVideoUrl(e.target.value); setSavedVideo(false); }}
            style={{ flex: 1, padding: '8px 10px', border: '1px solid oklch(0.85 0 0)',
                     borderRadius: 8, fontSize: 13 }}
          />
          <button className="btn btn--solid" disabled={savingVideo} onClick={saveVideo}>
            {savingVideo ? 'Salvataggio…' : 'Salva URL'}
          </button>
        </div>
        {savedVideo && <span style={{ fontSize: 12, color: 'oklch(0.45 0.13 145)' }}>✓ Salvato</span>}
      </div>

      <div style={{ marginBottom: 16, borderTop: '1px solid oklch(0.92 0 0)', paddingTop: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>APE (PDF)</div>
        <AssetSlot
          kind="ape"
          accept="application/pdf"
          present={apeStatus.present}
          size={apeStatus.size}
          uploading={uploading === 'ape'}
          onUpload={(f) => handleUpload('ape', f)}
          onDelete={() => handleDelete('ape')}
        />
      </div>

      <div style={{ marginBottom: 16, borderTop: '1px solid oklch(0.92 0 0)', paddingTop: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Planimetria standard (PDF)</div>
        <div style={{ fontSize: 12, color: 'oklch(0.5 0 0)', marginBottom: 8 }}>
          File PDF caricato al posto della planimetria catastale allegata a PVP. Marina 2026-07-04:
          "usare sempre il file standard della planimetria, non quella allegata su PVP".
        </div>
        <AssetSlot
          kind="planimetria"
          accept="application/pdf"
          present={planimetriaStatus.present}
          size={planimetriaStatus.size}
          uploading={uploading === 'planimetria'}
          onUpload={(f) => handleUpload('planimetria', f)}
          onDelete={() => handleDelete('planimetria')}
        />
      </div>

      <div style={{ borderTop: '1px solid oklch(0.92 0 0)', paddingTop: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>9 foto fisse</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
          {fotoSlots.map((i) => {
            const kind = `foto_${i}`;
            const st = fotoStatus[i] ?? { present: false };
            return (
              <div key={i} style={{ border: '1px dashed oklch(0.85 0 0)', borderRadius: 8, padding: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Foto {i}</div>
                <AssetSlot
                  kind={kind}
                  accept="image/jpeg,image/png"
                  present={st.present}
                  size={st.size}
                  uploading={uploading === kind}
                  onUpload={(f) => handleUpload(kind, f)}
                  onDelete={() => handleDelete(kind)}
                  compact
                />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function AssetSlot({ kind, accept, present, size, uploading, onUpload, onDelete, compact = false }) {
  const fileRef = useRef(null);
  const sizeStr = size ? (size > 1024*1024 ? (size/1024/1024).toFixed(1) + ' MB' : (size/1024).toFixed(0) + ' KB') : null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
      <span style={{ flex: 1, color: present ? 'oklch(0.35 0.13 145)' : 'oklch(0.55 0 0)' }}>
        {present ? `✓ Caricato${sizeStr ? ` · ${sizeStr}` : ''}` : '— vuoto'}
      </span>
      <input
        ref={fileRef}
        type="file"
        accept={accept}
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onUpload(f);
          e.target.value = '';
        }}
      />
      <button type="button" className="btn btn--ghost" style={{ fontSize: 11, padding: '4px 8px' }}
              disabled={uploading} onClick={() => fileRef.current?.click()}>
        {uploading ? '…' : present ? 'Sostituisci' : 'Carica'}
      </button>
      {present && (
        <button type="button" className="btn btn--ghost" style={{ fontSize: 11, padding: '4px 8px', color: 'oklch(0.55 0.13 25)' }}
                disabled={uploading} onClick={onDelete}>
          ✕
        </button>
      )}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0',
                  borderBottom: '1px solid oklch(0.92 0 0)', gap: 16 }}>
      <span style={{ color: 'oklch(0.50 0 0)', fontSize: 13 }}>{label}</span>
      <span style={{ fontSize: 13 }}>{children}</span>
    </div>
  );
}
function Tag({ tone, children }) {
  return <span style={tagStyle(tone)}>{children}</span>;
}
function Panel({ tone, children }) {
  const tones = {
    info:   { bg: 'oklch(0.96 0.03 240)', bd: 'oklch(0.78 0.10 240)', fg: 'oklch(0.32 0.13 240)' },
    action: { bg: 'oklch(0.97 0.04 90)',  bd: 'oklch(0.80 0.13 90)',  fg: 'oklch(0.30 0.13 90)'  },
    ok:     { bg: 'oklch(0.96 0.05 145)', bd: 'oklch(0.70 0.13 145)', fg: 'oklch(0.30 0.13 145)' },
    err:    { bg: 'oklch(0.96 0.04 25)',  bd: 'oklch(0.75 0.12 25)',  fg: 'oklch(0.35 0.15 25)'  },
  }[tone] || { bg: '#f7f7f7', bd: '#ddd', fg: '#333' };
  return <div style={{
    background: tones.bg, border: `1px solid ${tones.bd}`, color: tones.fg,
    padding: '14px 16px', borderRadius: 10, fontSize: 13.5, marginTop: 12,
  }}>{children}</div>;
}
function StepIndicator({ state }) {
  const steps = [
    { id: 's1', label: 'Login', match: ['starting', 'logging_in'] },
    { id: 's2', label: 'Conferma email', match: ['awaiting_email_confirm'] },
    { id: 's3', label: 'OTP', match: ['requesting_otp', 'awaiting_otp_code', 'submitting_otp'] },
    { id: 's4', label: 'Completato', match: ['dumping_cookies', 'completed'] },
  ];
  const order = ['starting','logging_in','awaiting_email_confirm','requesting_otp','awaiting_otp_code','submitting_otp','dumping_cookies','completed'];
  const idx = order.indexOf(state);
  return (
    <div style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
      {steps.map((s, i) => {
        const minIdx = Math.min(...s.match.map(m => order.indexOf(m)));
        const active = s.match.includes(state);
        const done = idx > Math.max(...s.match.map(m => order.indexOf(m)));
        return (
          <div key={s.id} style={{
            flex: 1,
            background: done ? 'oklch(0.85 0.13 145)' : active ? 'oklch(0.78 0.13 90)' : 'oklch(0.92 0 0)',
            color: (done || active) ? 'white' : 'oklch(0.45 0 0)',
            padding: '6px 8px', borderRadius: 6, fontSize: 11, textAlign: 'center', fontWeight: 500,
          }}>{done ? '✓ ' : ''}{s.label}</div>
        );
      })}
    </div>
  );
}

function fmtDate(iso) {
  try { return new Date(iso).toLocaleString('it-IT'); } catch { return iso; }
}

const card = { background: 'white', border: '1px solid oklch(0.92 0 0)', borderRadius: 12, padding: 20, maxWidth: 720 };
const cardHead = { marginBottom: 16 };
const cardTitle = { fontSize: 16, fontWeight: 600 };
const cardSub = { fontSize: 13, color: 'oklch(0.50 0 0)', marginTop: 4, lineHeight: 1.5 };
const muted = { color: 'oklch(0.55 0 0)', fontSize: 13 };
const statusBox = { background: 'oklch(0.985 0 0)', borderRadius: 8, padding: '4px 14px', border: '1px solid oklch(0.94 0 0)' };
const fieldRow = { marginBottom: 12 };
const lbl = { display: 'block', fontSize: 12.5, color: 'oklch(0.40 0 0)', marginBottom: 6, fontWeight: 500 };
const input = { width: '100%', padding: '8px 12px', border: '1px solid oklch(0.85 0 0)', borderRadius: 8, fontSize: 14 };
const textarea = { width: '100%', padding: '10px 12px', border: '1px solid oklch(0.85 0 0)', borderRadius: 8, fontSize: 12.5, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', lineHeight: 1.5, resize: 'vertical', boxSizing: 'border-box' };
const ruleRow = { border: '1px solid oklch(0.92 0 0)', borderRadius: 8, padding: '10px 12px', marginBottom: 8, background: 'oklch(0.985 0 0)' };
const defSummary = { fontSize: 12, cursor: 'pointer', color: 'oklch(0.45 0 0)' };
const defPre = { whiteSpace: 'pre-wrap', fontSize: 11.5, lineHeight: 1.45, background: 'oklch(0.985 0 0)', border: '1px solid oklch(0.92 0 0)', borderRadius: 8, padding: 10, marginTop: 6, maxHeight: 260, overflow: 'auto' };
const errBox = { background: 'oklch(0.96 0.04 25)', border: '1px solid oklch(0.75 0.12 25)', color: 'oklch(0.35 0.15 25)', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginTop: 12 };
const h3 = { fontSize: 14, fontWeight: 600, margin: '0 0 4px 0' };
const sub = { fontSize: 12.5, color: 'oklch(0.50 0 0)', margin: '0 0 12px 0', lineHeight: 1.5 };
const tagStyle = (tone) => ({
  display: 'inline-block', fontSize: 12,
  background: tone === 'ok' ? 'oklch(0.95 0.05 145)' : 'oklch(0.96 0.04 70)',
  color:      tone === 'ok' ? 'oklch(0.35 0.13 145)' : 'oklch(0.35 0.13 70)',
  border:     tone === 'ok' ? '1px solid oklch(0.78 0.13 145)' : '1px solid oklch(0.80 0.12 70)',
  padding: '3px 9px', borderRadius: 100,
});


// ============================================================
// Impostazioni Prospect (BTC mobile redesign)
// ============================================================
function ProspectImpostazioni({ user }) {
  const [waNotifEnabled, setWaNotifEnabled] = useState(() => {
    try { return localStorage.getItem('pr_wa_notif') !== '0'; } catch (_) { return true; }
  });
  const toggle = () => {
    setWaNotifEnabled((v) => {
      const next = !v;
      try {
        localStorage.setItem('pr_wa_notif', next ? '1' : '0');
        window.dispatchEvent(new Event('pr-wa-notif-change'));
      } catch (_) {}
      return next;
    });
  };
  return (
    <main className="content prospect-redesign" style={{ padding: '20px 16px 32px', maxWidth: 560, margin: '0 auto' }}>
      <h1 style={{ fontSize: 22, fontWeight: 800, color: '#16181C', margin: 0 }}>Impostazioni</h1>
      <p style={{ fontSize: 13, color: '#6A6E74', marginTop: 4 }}>{user?.email}</p>

      <section style={{
        marginTop: 22, background: '#fff', borderRadius: 18,
        padding: 18, boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 42, height: 42, borderRadius: 12,
            background: waNotifEnabled ? 'rgba(37,211,102,0.12)' : '#F3F2EE',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none',
          }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill={waNotifEnabled ? '#25D366' : '#9A9EA4'}>
              <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91C21.96 6.45 17.5 2 12.04 2zm5.8 14.06c-.25.7-1.44 1.33-1.99 1.41-.51.08-1.16.11-1.87-.12-.43-.14-.98-.32-1.69-.62-2.97-1.28-4.91-4.27-5.06-4.47-.15-.2-1.21-1.61-1.21-3.07s.77-2.18 1.04-2.48c.27-.3.59-.37.79-.37l.57.01c.18.01.43-.07.67.51.25.6.84 2.06.91 2.21.07.15.12.32.02.52-.1.2-.15.32-.3.49-.15.17-.31.39-.45.52-.15.15-.3.31-.13.61.17.3.76 1.25 1.63 2.03 1.12 1 2.06 1.31 2.36 1.46.3.15.47.12.65-.07.18-.2.74-.86.94-1.16.2-.3.4-.25.67-.15.27.1 1.71.81 2.01.96.3.15.5.22.57.35.07.12.07.72-.18 1.42z"/>
            </svg>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#16181C' }}>Notifiche WhatsApp</div>
            <div style={{ fontSize: 12.5, color: '#6A6E74', marginTop: 2 }}>
              {waNotifEnabled
                ? 'Ricevi su WhatsApp i nuovi immobili scoperti dal radar.'
                : 'Notifiche disattivate. Non riceverai avvisi sui nuovi immobili.'}
            </div>
          </div>
          <button onClick={toggle} aria-pressed={waNotifEnabled}
                  style={{
                    width: 46, height: 28, borderRadius: 14,
                    background: waNotifEnabled ? '#25D366' : '#CFCDC6',
                    border: 0, cursor: 'pointer', position: 'relative',
                    transition: 'background .25s ease', flex: 'none',
                  }}>
            <span style={{
              position: 'absolute', top: 3, left: waNotifEnabled ? 21 : 3,
              width: 22, height: 22, borderRadius: '50%', background: '#fff',
              boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
              transition: 'left .22s ease',
            }} />
          </button>
        </div>
      </section>
    </main>
  );
}
